import 'react-notifications/lib/notifications.css';
import './chrome-embed.css';

import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { DataAPIContext, DataProviderFactory } from './api/';
import React, { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Activity from './activity';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { BreakpointProvider } from './components/breakpoints';
import Chat from './chat';
import ChatThemeScope from './chat/ChatThemeScope';
import { ContextStore } from './store/contextStore';
import Dashboard from './dashboard';
import { DndProvider } from 'react-dnd';
import Explore from './explore';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MainLayout from './layout/main';
import Notification from './notifications';
import McpConnections from './mcp_connections';
import Account from './account';
import { NotificationContainer } from 'react-notifications';
import PreTrip from './pretrip';
import Settings from './settings';
import SignIn from './auth/signin';
import PasswordResetPage from './auth/PasswordResetPage';
import OAuthHandoffPage from './auth/OAuthHandoffPage';
import { OAUTH_HANDOFF_PATH } from './auth/oauthHandoff';
import SandboxHandoffPage from './auth/SandboxHandoffPage';
import { SANDBOX_HANDOFF_PATH } from './auth/sandboxHandoff';
import SignupApp from './signup/SignupApp';
import { isSignupHost } from './signup/host';
import { SnackbarProvider } from 'notistack';
// import { logout, selectIsAuthenticated, selectToken } from "./store/auth";
import ActivityCategories from './activity_categories';
import Airbnb from './airbnb';
import Tags from './tags';
import TourMedia from './tour_media';
import Trips from './trips';
import Users from './users';
import XmlImport from './xml_import';
import Developer from './developer';
import Onboarding, { OnboardingStatusProvider, SetupGate, stashSetupFlag } from './onboarding';
import Sync, { SYNC_PATH } from './sync';
import { LEGACY_REDIRECTS } from './developer/routes';
import OtaImport from './ota_import';
import BusTours from './bus_tours';
import config from './config';
import { PreferencesProvider } from './preferences';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material';
import { Auth0Provider } from '@auth0/auth0-react';
import Mainentance from './Mainentance';
import { isDashboardChromeEmbed } from './chromeEmbed';

const useStyles = makeStyles(() => ({
    snackbar: {
        zIndex: '1000000 !important',
    },
}));

const App = () => {
    const { token, logout, checkIsAuthenticated, auth_method, tourOperator } = useContext(ContextStore);
    const is_authenticated = checkIsAuthenticated();
    const theme = useTheme();
    const classes = useStyles();
    const [chromeEmbedNotistackHost, setChromeEmbedNotistackHost] = useState(null);
    const chromeEmbed = isDashboardChromeEmbed();

    useLayoutEffect(() => {
        if (!chromeEmbed) {
            return undefined;
        }
        document.body.classList.add('cp-dashboard-chrome-embed');
        const el = document.createElement('div');
        el.setAttribute('data-cp-chrome-embed-notistack-host', '1');
        el.setAttribute('aria-hidden', 'true');
        el.style.cssText =
            'position:fixed!important;left:0!important;top:0!important;width:0!important;height:0!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;';
        document.body.appendChild(el);
        setChromeEmbedNotistackHost(el);
        return () => {
            document.body.classList.remove('cp-dashboard-chrome-embed');
            if (el.parentNode) {
                el.parentNode.removeChild(el);
            }
            setChromeEmbedNotistackHost(null);
        };
    }, [chromeEmbed]);

    // Keep the data provider identity stable across re-renders: it sits in DataAPIContext
    // and components key fetch effects on it, so a fresh object every render makes every
    // mounted list silently refetch. Rebuild only when the token actually changes.
    const logoutRef = useRef(logout);
    logoutRef.current = logout;
    // The token is read through a ref so rotation doesn't rebuild the provider: the
    // provider is created once per session and its identity never changes.
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const hasToken = !!token;
    const dp = useMemo(() => {
        if (!is_authenticated || !hasToken) {
            return null;
        }
        const provider = DataProviderFactory({
            httpClient: {
                getToken: () => tokenRef.current,
            },
            apiUrl: config.apiUrl,
            provider: {
                onAuthRequest: (...args) => {
                    logoutRef.current();
                },
            },
        });
        window.dp = provider;
        return provider;
    }, [is_authenticated, hasToken]);

    const queries = {
        portrait: '(orientation: portrait)',
        landscape: '(orientation: landscape)',
        xs: theme.breakpoints.up('xs').substr(7), // strip @media
        sm: theme.breakpoints.up('sm').substr(7), // strip @media
        md: theme.breakpoints.up('md').substr(7), // strip @media
        lg: theme.breakpoints.up('lg').substr(7), // strip @media
        xl: theme.breakpoints.up('xl').substr(7), // strip @media
    };

    if (config.maintenance) {
        return (<Mainentance />);
    }

    // The self-signup app (backend plan 2026-10-17-dashboard-self-signup-invite-links §16): the same
    // build on signup.<dashboard domain>, before the auth gate — an invite link is the only key there.
    if (isSignupHost()) {
        return (<SignupApp />);
    }

    // Public, auth-independent landing page for staff password-reset links
    // issued from the ops console. Handled before the auth gate so it renders
    // whether or not the visitor has a session.
    if (window.location.pathname === '/reset-password') {
        return (<PasswordResetPage />);
    }

    // The MCP sign-in handoff (backend metaplan A7c), also before the auth gate: it keeps the
    // authorization server's request across the sign-in (the Auth0 round trip drops the query
    // string) and shows the sign-in itself when there is no session.
    if (window.location.pathname === OAUTH_HANDOFF_PATH) {
        return (<OAuthHandoffPage />);
    }

    // A sandbox sign-in from production (DSEC D3), also before the auth gate: the one-time code in the fragment
    // buys this sandbox dashboard a session, whatever session (if any) it held before.
    if (window.location.pathname === SANDBOX_HANDOFF_PATH) {
        return (<SandboxHandoffPage />);
    }

    if (is_authenticated) {
        // The sign-up link's `?setup=1` (SETUP-GATE D1), read here before MainLayout's redirects drop the query: kept
        // per tenant for SetupGate, and `setup` / `login_hint` leave the URL. Idempotent.
        stashSetupFlag(tourOperator?.id);
        let intLayout = (
            <DataAPIContext.Provider value={dp}>
                <PreferencesProvider>
                <OnboardingStatusProvider>
                {/* "Setting up your account…" for a signed-up admin whose permissions are not there yet (SETUP-GATE
                    D2): above the Router, so no page, redirect or poll runs before the account is ready. */}
                <SetupGate>
                <BrowserRouter>
                    {/* Above the Switch, not around the /chat route: a wrapper on one route would
                        make React remount MainLayout (sidebar state, polling) on every move in or
                        out of chat. The scope itself applies a theme only on /chat. */}
                    <ChatThemeScope>
                    <Switch>
                        <Route path="/:id(\d+)" exact>
                            <Redirect to="/" />
                        </Route>
                        <Route path="/dashboard">
                            <MainLayout component={Dashboard.Page} />
                        </Route>
                        <Route path="/explore/texts">
                            <MainLayout component={Explore.TitleTexts.Page} />
                        </Route>
                        <Route path="/explore/filters">
                            <MainLayout component={Explore.Filters.Page} />
                        </Route>
                        <Route path="/explore/regions">
                            <MainLayout component={Explore.Regions.Page} />
                        </Route>
                        <Route path="/explore/itineraries">
                            <MainLayout component={Explore.Itineraries.Page} />
                        </Route>
                        <Route path="/pretrip">
                            <MainLayout component={PreTrip.Page} />
                        </Route>
                        <Route path="/tags">
                            <MainLayout component={Tags.Page} />
                        </Route>
                        <Route path="/activity-categories">
                            <MainLayout component={ActivityCategories.Page} />
                        </Route>
                        <Route path="/trip">
                            <MainLayout component={Trips.Page} />
                        </Route>
                        <Route path="/activity">
                            <MainLayout component={Activity.Page} />
                        </Route>
                        <Route path="/bus-tours">
                            <MainLayout component={BusTours.Page} />
                        </Route>
                        <Route path="/live-map">
                            <MainLayout component={Users.LiveMap} />
                        </Route>
                        {/* The user's own Sign-in security (dashboard MFA), from the user menu; before the
                            generic /user/:id pages. */}
                        <Route path="/account/security" exact>
                            <MainLayout component={Account.Security.Page} />
                        </Route>
                        <Route path="/user">
                            <MainLayout component={Users.Page} />
                        </Route>
                        <Route path="/chat/tour-priority">
                            <Redirect to="/settings/tour-chat-priority" />
                        </Route>
                        <Route path="/chat">
                            <MainLayout component={Chat.Page} />
                        </Route>
                        {/* API keys and Documentation moved to the Developer section (S7b, SBX D9). */}
                        {LEGACY_REDIRECTS.map(([from, to]) => (
                            <Route key={from} path={from}>
                                <Redirect to={to} />
                            </Route>
                        ))}
                        <Route path="/developer">
                            <MainLayout component={Developer.Page} />
                        </Route>
                        {/* The client's own easy onboarding (self-signup plan §16). */}
                        <Route path="/onboarding">
                            <MainLayout component={Onboarding.Page} />
                        </Route>
                        {/* The client's own product / aux info syncs (backend self-signup D17). */}
                        <Route path={SYNC_PATH}>
                            <MainLayout component={Sync.Page} />
                        </Route>
                        <Route path="/import/otas">
                            <MainLayout component={OtaImport.Page} />
                        </Route>
                        <Route path="/import">
                            <MainLayout component={XmlImport.Page} />
                        </Route>
                        <Route path="/notifications">
                            <MainLayout component={Notification.Page} />
                        </Route>
                        <Route path="/mcp-connections" exact>
                            <MainLayout component={McpConnections.Page} />
                        </Route>
                        <Route path="/settings/general">
                            <MainLayout component={Settings.General.Page} />
                        </Route>
                        <Route path="/settings/checkin">
                            <MainLayout component={Settings.Checkin.Page} />
                        </Route>
                        {/* The MCP auto-apply switch (client-MCP Q26) and the guest-data opt-in (P1b-06). */}
                        <Route path="/settings/ai-editing">
                            <MainLayout component={Settings.AIEditing.Page} />
                        </Route>
                        {/* The company's AI vocabulary (client-MCP §12.5). */}
                        <Route path="/settings/ai-vocabulary">
                            <MainLayout component={Settings.AIVocabulary.Page} />
                        </Route>
                        <Route path="/settings/ai">
                            <MainLayout component={Settings.TrainingMaterials.Page} />
                        </Route>
                        <Route path="/settings/sync-bookings">
                            <MainLayout component={Settings.SyncBookings.Page} />
                        </Route>
                        <Route path="/settings/sync-orders">
                            <MainLayout component={Settings.SyncOrders.Page} />
                        </Route>
                        <Route path="/settings/airbnb">
                            <MainLayout component={Airbnb.Page} />
                        </Route>
                        <Route path="/settings/split-ota-bookings">
                            <MainLayout component={Settings.SplitOTABookings.Page} />
                        </Route>
                        <Route path="/settings/tour-chat-priority">
                            <MainLayout component={Settings.TourChatPriority.Page} />
                        </Route>
                        <Route path="/settings/web-widget">
                            <MainLayout component={Settings.WebWidget.Page} />
                        </Route>
                        <Route path="/settings/licensees">
                            <MainLayout component={Settings.Licensees.Page} />
                        </Route>
                        <Route path="/settings/email-domain">
                            <MainLayout component={Settings.EmailDomains.Page} />
                        </Route>
                        <Route path="/waivers" exact>
                            <MainLayout component={Settings.Waivers.Page} />
                        </Route>
                        <Route path="/waivers/new" exact>
                            <MainLayout component={Settings.Waivers.EditPage} />
                        </Route>
                        <Route path="/waivers/:id" exact>
                            <MainLayout component={Settings.Waivers.EditPage} />
                        </Route>
                        <Route path="/waivers/:id/preview" exact>
                            <MainLayout component={Settings.Waivers.PreviewPage} />
                        </Route>
                        <Route path="/settings/reviews">
                            <MainLayout component={Settings.Reviews.Page} />
                        </Route>
                        <Route path="/analytics">
                            <MainLayout component={Settings.SentimentAnalysis.Page} />
                        </Route>
                        <Route path="/marketing-suite/media/:tourId" exact>
                            <MainLayout component={TourMedia.Detail} />
                        </Route>
                        <Route path="/marketing-suite/media" exact>
                            <MainLayout component={TourMedia.List} />
                        </Route>
                        <Route exact path="/settings">
                            <MainLayout component={Settings.General.Page} />
                        </Route>
                        <Route exact path="/">
                            <MainLayout component={Dashboard.List} />
                        </Route>
                    </Switch>
                    </ChatThemeScope>
                    <NotificationContainer />
                </BrowserRouter>
                </SetupGate>
                </OnboardingStatusProvider>
                </PreferencesProvider>
            </DataAPIContext.Provider>
        );
        if (!!auth_method) {
            intLayout = (<Auth0Provider domain={auth_method.domain} clientId={auth_method.client_id} authorizationParams={{ redirect_uri: window.location.origin }}>
                {intLayout}
            </Auth0Provider>);
        }
        return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
            <DndProvider backend={HTML5Backend}>
            <SnackbarProvider
                dense={true}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={2500}
                classes={{ containerRoot: classes.snackbar }}
                domRoot={chromeEmbed && chromeEmbedNotistackHost ? chromeEmbedNotistackHost : undefined}
            >
            <BreakpointProvider queries={queries}>
                {intLayout}
            </BreakpointProvider>
            </SnackbarProvider>
            </DndProvider>
            </LocalizationProvider>
        );
    } else {
        return (
            <BrowserRouter>
                <Route path="*">
                    <SignIn />
                </Route>
            </BrowserRouter>
        );
    }
};

export default App;
